/**
 * NotasGerais — anotacoes livres do vendedor (categoria GERAL).
 *
 * Adicionar / editar / fixar / excluir. Fixadas sobem ao topo (badge).
 * Recebe as notas GERAL do `spin.GERAL` do detalhe + callbacks.
 *
 * `podeEditar` (lead:edit) governa toda escrita: o backend exige lead:edit em
 * criar, editar, fixar e excluir nota (CRM-23). Sem a permissao o bloco e so
 * leitura — antes mostrava os botoes e cada clique levava 403.
 */
import React, { useMemo, useState } from "react";
import { FiBookmark, FiCheck, FiEdit2, FiTrash2, FiX } from "react-icons/fi";

import Block from "../../../components/Block";
import AsyncButton from "../../../components/AsyncButton";
import UserAvatar from "../../../components/UserAvatar";
import { tempoRel } from "../crmHelpers";

import {
  MiniButton,
  NotaItem,
  NotaMeta,
  NotaTexto,
} from "./styles";
import { Textarea } from "../styles";

function NotaLinha({ nota, onEditar, onFixar, onExcluir, podeEditar }) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(nota.texto || "");

  const salvar = async () => {
    const t = texto.trim();
    if (!t) return; // botao desabilitado quando vazio
    await onEditar(nota.id, t);
    setEditando(false);
  };

  if (editando && podeEditar) {
    return (
      <NotaItem>
        <Textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={2}
          autoFocus
        />
        <NotaMeta>
          <MiniButton
            type="button"
            onClick={() => {
              setEditando(false);
              setTexto(nota.texto || "");
            }}
          >
            <FiX size={12} aria-hidden="true" /> Cancelar
          </MiniButton>
          <AsyncButton
            variant="secondary"
            onClick={salvar}
            successMessage="Nota atualizada."
            silentError
            disabled={!texto.trim()}
          >
            <FiCheck size={12} aria-hidden="true" /> Salvar
          </AsyncButton>
        </NotaMeta>
      </NotaItem>
    );
  }

  return (
    <NotaItem>
      <NotaTexto>
        {nota.fixada && (
          <span
            className="badge warning no-dot"
            style={{ marginRight: 6 }}
          >
            <FiBookmark size={10} aria-hidden="true" /> Fixada
          </span>
        )}
        {nota.texto}
      </NotaTexto>
      <NotaMeta>
        <UserAvatar src={nota.autor_foto_url} nome={nota.autor_nome} size="xs" />
        <span>{nota.autor_nome || "—"}</span>
        <span title={nota.created_at}>· {tempoRel(nota.created_at)}</span>
        {podeEditar && (
          <>
            <MiniButton
              type="button"
              onClick={() => onFixar(nota.id, !nota.fixada)}
              title={nota.fixada ? "Desafixar" : "Fixar no topo"}
              style={{ marginLeft: "auto" }}
            >
              <FiBookmark size={12} aria-hidden="true" />
            </MiniButton>
            <MiniButton type="button" onClick={() => setEditando(true)} title="Editar">
              <FiEdit2 size={12} aria-hidden="true" />
            </MiniButton>
            <MiniButton
              type="button"
              $tone="danger"
              onClick={() => onExcluir(nota.id)}
              title="Excluir"
            >
              <FiTrash2 size={12} aria-hidden="true" />
            </MiniButton>
          </>
        )}
      </NotaMeta>
    </NotaItem>
  );
}

function NotasGerais({
  notas,
  onAdicionar,
  onEditar,
  onFixar,
  onExcluir,
  podeEditar = false,
  collapsible,
  defaultCollapsed,
}) {
  const [texto, setTexto] = useState("");

  const ordenadas = useMemo(() => {
    const lista = Array.isArray(notas) ? [...notas] : [];
    // Fixadas primeiro; dentro do grupo, mais recentes no topo.
    lista.sort((a, b) => {
      if (Boolean(b.fixada) !== Boolean(a.fixada)) {
        return b.fixada ? 1 : -1;
      }
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
    return lista;
  }, [notas]);

  const adicionar = async () => {
    const t = texto.trim();
    if (!t) return; // botao desabilitado quando vazio
    await onAdicionar(t);
    setTexto("");
  };

  return (
    <Block
      title={`Notas gerais (${ordenadas.length})`}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
    >
      {ordenadas.map((n) => (
        <NotaLinha
          key={n.id}
          nota={n}
          onEditar={onEditar}
          onFixar={onFixar}
          onExcluir={onExcluir}
          podeEditar={podeEditar}
        />
      ))}

      {!podeEditar && ordenadas.length === 0 && (
        <span style={{ fontSize: "0.85rem", color: "var(--ink-3)" }}>
          Nenhuma nota.
        </span>
      )}

      {podeEditar && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            paddingTop: 8,
            borderTop: "1px solid var(--divider)",
          }}
        >
          <Textarea
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={2}
            placeholder="Anote uma observação interna sobre este lead..."
          />
          <AsyncButton
            variant="secondary"
            onClick={adicionar}
            successMessage="Nota adicionada."
            silentError
            disabled={!texto.trim()}
            style={{ alignSelf: "flex-start" }}
          >
            Adicionar nota
          </AsyncButton>
        </div>
      )}
    </Block>
  );
}

export default NotasGerais;
